import * as THREE from 'three';
import { t } from '../../shared/i18n.js';

// The way to where the player is going: a ribbon drawn on the road in the world, the same line
// on the corner map and the big map, and a short turn-by-turn hint ("Turn left in 80 m").
const WIDTH = 1.4; // metres, the ribbon on the ground
const LIFT = 0.32; // metres above the ground
const COLOUR = '#a98bff';

export class Route {
  constructor(scene, heightmap) {
    this.scene = scene;
    this.heightmap = heightmap;
    this.points = null; // [[x, z], ...] from the player to the goal
    this.metres = 0;
    this.to = null; // { n, x, z }
    this.mesh = null;
    this.material = new THREE.MeshBasicMaterial({ color: COLOUR, transparent: true, opacity: 0.85, depthWrite: false, side: THREE.DoubleSide, fog: false });
  }

  get active() {
    return !!this.points;
  }

  set(points, metres, to) {
    this.clear();
    if (!points || points.length < 2) return;
    this.points = points;
    this.metres = metres;
    this.to = to;
    this.build();
  }

  clear() {
    this.points = null;
    this.to = null;
    if (this.mesh) {
      this.scene.remove(this.mesh);
      this.mesh.geometry.dispose();
      this.mesh = null;
    }
  }

  // A flat strip along the points, hugging the ground.
  build() {
    const pts = this.points;
    const pos = [];
    const idx = [];
    const h = (x, z) => this.heightmap.heightAt(x, z) + LIFT;
    for (let k = 0; k < pts.length; k++) {
      const [x, z] = pts[k];
      const [px, pz] = pts[Math.max(0, k - 1)];
      const [nx, nz] = pts[Math.min(pts.length - 1, k + 1)];
      let dx = nx - px;
      let dz = nz - pz;
      const len = Math.hypot(dx, dz) || 1;
      dx /= len;
      dz /= len;
      // the side vector, turned a quarter
      const sx = -dz * WIDTH / 2;
      const sz = dx * WIDTH / 2;
      pos.push(x + sx, h(x + sx, z + sz), z + sz, x - sx, h(x - sx, z - sz), z - sz);
      if (k > 0) {
        const a = (k - 1) * 2;
        idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setIndex(idx);
    this.mesh = new THREE.Mesh(geo, this.material);
    this.mesh.renderOrder = 2;
    this.mesh.frustumCulled = false;
    this.scene.add(this.mesh);
  }

  // Where the player is along the route: the index of the nearest segment and how far off it.
  nearest(x, z) {
    const pts = this.points;
    let best = 0;
    let bestD = Infinity;
    let bestT = 0;
    for (let k = 0; k < pts.length - 1; k++) {
      const [ax, az] = pts[k];
      const [bx, bz] = pts[k + 1];
      const vx = bx - ax;
      const vz = bz - az;
      const len2 = vx * vx + vz * vz || 1;
      const tt = Math.max(0, Math.min(1, ((x - ax) * vx + (z - az) * vz) / len2));
      const d = (ax + vx * tt - x) ** 2 + (az + vz * tt - z) ** 2;
      if (d < bestD) {
        bestD = d;
        best = k;
        bestT = tt;
      }
    }
    return { segment: best, t: bestT, off: Math.sqrt(bestD) };
  }

  // Distance left along the route from the player, in metres.
  left(x, z) {
    const { segment, t: tt } = this.nearest(x, z);
    const pts = this.points;
    let m = (1 - tt) * Math.hypot(pts[segment + 1][0] - pts[segment][0], pts[segment + 1][1] - pts[segment][1]);
    for (let k = segment + 1; k < pts.length - 1; k++) m += Math.hypot(pts[k + 1][0] - pts[k][0], pts[k + 1][1] - pts[k][1]);
    return m;
  }

  // The next turn ahead: { side: 'left'|'right'|null, metres }. A bend of more than 35 degrees counts.
  nextTurn(x, z) {
    const { segment } = this.nearest(x, z);
    const pts = this.points;
    let m = Math.hypot(pts[segment + 1][0] - x, pts[segment + 1][1] - z);
    for (let k = segment + 1; k < pts.length - 1; k++) {
      const ax = pts[k][0] - pts[k - 1][0];
      const az = pts[k][1] - pts[k - 1][1];
      const bx = pts[k + 1][0] - pts[k][0];
      const bz = pts[k + 1][1] - pts[k][1];
      const cross = ax * bz - az * bx;
      const dot = ax * bx + az * bz;
      const angle = Math.atan2(cross, dot);
      if (Math.abs(angle) > 0.61) return { side: angle > 0 ? 'right' : 'left', metres: m };
      m += Math.hypot(bx, bz);
      if (m > 600) break;
    }
    return { side: null, metres: m };
  }

  // The line for the HUD: "Food Market · 1.2 km · turn left in 80 m".
  text(x, z) {
    if (!this.points) return '';
    const left = this.left(x, z);
    const dist = left < 1000 ? t('{n} m', { n: Math.round(left / 10) * 10 }) : t('{n} km', { n: (left / 1000).toFixed(1) });
    const turn = this.nextTurn(x, z);
    let hint = '';
    if (turn.side && turn.metres < 160) hint = ' · ' + t(turn.side === 'left' ? 'turn left in {n} m' : 'turn right in {n} m', { n: Math.max(10, Math.round(turn.metres / 10) * 10) });
    else if (left < 40) hint = ' · ' + t('you are there');
    return `${this.to ? this.to.n : ''} · ${dist}${hint}`;
  }
}
